import { DateTime } from "luxon";
import { AssetTypes } from "../../../../public/icons-v1_1";
import { PathPickBadgeColorVariant } from "@/features/pathpick/components/ui/PathPickBadge";
import { FORMAT_DASH, FORMAT_DASH_HM } from "@/shared/constants/date";
import {
  PathPickProductRequestKind,
  PathPickRequestItemKind,
} from "@/features/pathpick/types";
import {
  PathPickItemOption,
  PathPickProductRequestChannel,
  PathPickProductRequestChannelResult,
  PathPickProductRequestItem,
  PathPickProductRequestResult,
  PathPickProductRequestStatus,
  PathPickProductSyncDeletedBy,
  PathPickProductSyncStatus,
  PathPickRequestDetailItem,
  PathPickRequestErrorType,
  PathPickRequestItem,
  PathPickRequestItemStatus,
  PathPickRequestStatus,
} from "@/features/pathpick/types";
import {
  PathPickProductRequestListResponse,
  PathPickRequestsResponse,
} from "@/features/pathpick/types/response";

// 서버 타임스탬프는 모두 UTC지만 엔드포인트에 따라 "Z"가 붙기도 하고 빠지기도 한다
// (요청 상세의 items[].createdAt은 "Z"가 없고, 상품별 처리 내역의 requestedAt은 있다).
// luxon은 오프셋 없는 문자열을 "이미 로컬 시각"으로 가정해버려 KST에서 9시간 뒤처지므로,
// 읽는 시간대를 UTC로 고정한 뒤 로컬로 변환해 두 모양이 같은 순간이 되게 한다.
// "Z"가 붙은 문자열은 문자열의 오프셋이 우선해 결과가 바뀌지 않는다.
const fromServerISO = (iso: string) =>
  DateTime.fromISO(iso, { zone: "utc" }).toLocal();

// ISO("2026-07-20T01:40:29") → "2026-07-20 10:40"(KST)
export const formatRequestDateTime = (iso: string) =>
  fromServerISO(iso).toFormat(FORMAT_DASH_HM);

// ISO("2026-07-20T14:12:29") → "23:12"(KST). 날짜는 그룹 헤더가 이미 보여준다.
export const formatRequestTime = (iso: string) =>
  fromServerISO(iso).toFormat("HH:mm");

// 진행중 = PENDING / PROCESSING. 그 외(QUOTA_EXCEEDED 포함)는 모두 종료 상태다.
// 폴링을 계속할지 가르는 판정이라, 상태가 늘면 여기서 컴파일 에러를 내 결정을 강제한다.
const IS_REQUEST_PENDING: Record<PathPickRequestStatus, boolean> = {
  PENDING: true,
  PROCESSING: true,
  COMPLETED: false,
  PARTIAL_COMPLETED: false,
  FAILED: false,
  QUOTA_EXCEEDED: false,
};

// 서버가 아직 매핑되지 않은 상태를 내려주면 종료로 본다 — 폴링이 영원히 도는 쪽보다 안전하다.
export const isPathPickRequestPending = (status: PathPickRequestStatus) =>
  IS_REQUEST_PENDING[status] ?? false;

export const hasPendingPathPickRequest = (
  response?: PathPickRequestsResponse
) =>
  (response?.items ?? []).some((item) => isPathPickRequestPending(item.status));

export interface PathPickRequestStatusMeta {
  // path_pick:sync_download_history 하위 키
  labelKey: string;
  colorVariant: PathPickBadgeColorVariant;
  leadingIcon?: AssetTypes;
}

// 같은 상태라도 구분(API 연동 / 엑셀 다운로드)에 따라 문구와 색이 함께 갈려서 메타를 쌍으로 둔다.
interface RequestStatusMetaByKind {
  // API 연동 요청
  sync: PathPickRequestStatusMeta;
  // 엑셀 다운로드 요청
  download: PathPickRequestStatusMeta;
}

// 진행중 — 옆에 붙는 스피너가 진행중임을 보여주므로 아이콘은 두지 않는다.
// 매핑되지 않은 상태의 폴백이기도 하다.
const PROCESSING_META_BY_KIND: RequestStatusMetaByKind = {
  sync: { labelKey: "status_sync_processing", colorVariant: "gray" },
  download: { labelKey: "status_file_processing", colorVariant: "gray" },
};

// 한도 초과는 구분과 무관하게 같은 표기라 한 객체를 양쪽에 쓴다.
const QUOTA_EXCEEDED_META: PathPickRequestStatusMeta = {
  labelKey: "status_quota_exceeded",
  colorVariant: "red",
  leadingIcon: "warning-circle",
};

// 요청 상태 → 배지 표기. 상태가 늘면 여기서 컴파일 에러가 나 표기를 정하고 지나가게 된다.
const REQUEST_STATUS_META: Record<
  PathPickRequestStatus,
  RequestStatusMetaByKind
> = {
  PENDING: PROCESSING_META_BY_KIND,
  PROCESSING: PROCESSING_META_BY_KIND,
  COMPLETED: {
    sync: {
      labelKey: "status_synced",
      colorVariant: "green",
      leadingIcon: "check",
    },
    download: {
      labelKey: "status_downloadable",
      colorVariant: "blue",
      leadingIcon: "check",
    },
  },
  PARTIAL_COMPLETED: {
    sync: { labelKey: "status_partial_sync", colorVariant: "yellow" },
    download: { labelKey: "status_partial_file", colorVariant: "yellow" },
  },
  FAILED: {
    sync: {
      labelKey: "status_sync_failed",
      colorVariant: "red",
      leadingIcon: "warning-circle",
    },
    download: {
      labelKey: "status_file_failed",
      colorVariant: "red",
      leadingIcon: "warning-circle",
    },
  },
  QUOTA_EXCEEDED: { sync: QUOTA_EXCEEDED_META, download: QUOTA_EXCEEDED_META },
};

// 서버가 아직 매핑되지 않은 상태를 내려주면 처리중으로 둔다 (가장 무해한 표기).
export const getPathPickRequestStatusMeta = (
  status: PathPickRequestStatus,
  isApiSync: boolean
): PathPickRequestStatusMeta => {
  const meta = REQUEST_STATUS_META[status] ?? PROCESSING_META_BY_KIND;
  return isApiSync ? meta.sync : meta.download;
};

// 행(상품) 상태 → 요청 단위 상태. 표기는 요청 단위 것을 그대로 재사용해
// 목록과 모달의 문구가 갈리지 않게 한다.
const ITEM_STATUS_TO_REQUEST_STATUS: Record<
  PathPickRequestItemStatus,
  PathPickRequestStatus
> = {
  SUCCESS: "COMPLETED",
  FAILED: "FAILED",
  QUOTA_EXCEEDED: "QUOTA_EXCEEDED",
};

// 행 상태가 없으면(= 아직 처리 전) 요청이 한도 초과로 끝났는지 보고, 그것도 아니면 처리중으로 둔다.
export const getPathPickRequestItemStatusMeta = (
  itemStatus: PathPickRequestItemStatus | null,
  requestStatus: PathPickRequestStatus,
  isApiSync: boolean
): PathPickRequestStatusMeta => {
  if (itemStatus) {
    return getPathPickRequestStatusMeta(
      ITEM_STATUS_TO_REQUEST_STATUS[itemStatus] ?? "PROCESSING",
      isApiSync
    );
  }
  return getPathPickRequestStatusMeta(
    requestStatus === "QUOTA_EXCEEDED" ? "QUOTA_EXCEEDED" : "PROCESSING",
    isApiSync
  );
};

export interface PathPickRequestDateGroup {
  // "2026-07-02"
  dateKey: string;
  items: PathPickRequestItem[];
}

// 신청일(requestedAt) 기준으로 묶는다. 정렬하지 않고 응답 순서(최신순)를 그대로 따르므로
// 페이지 경계에 걸친 같은 날짜는 각 페이지 안에서만 하나의 그룹이 된다.
export const groupPathPickRequestsByDate = (
  items: PathPickRequestItem[]
): PathPickRequestDateGroup[] => {
  const groups = new Map<string, PathPickRequestItem[]>();

  items.forEach((item) => {
    const dateKey = fromServerISO(item.requestedAt).toFormat(FORMAT_DASH);
    const group = groups.get(dateKey);
    if (group) {
      group.push(item);
      return;
    }
    groups.set(dateKey, [item]);
  });

  return Array.from(groups, ([dateKey, groupItems]) => ({
    dateKey,
    items: groupItems,
  }));
};

// "2026-07-02" → ko: "2026년 7월 2일" / 그 외: 로케일 기본 긴 날짜("July 2, 2026")
export const formatRequestDateGroupLabel = (
  dateKey: string,
  isKor: boolean,
  language: string
) => {
  const date = DateTime.fromISO(dateKey);
  return isKor
    ? date.setLocale("ko").toFormat("yyyy년 M월 d일")
    : date.setLocale(language).toLocaleString(DateTime.DATE_FULL);
};

// 신청 종류 → 상품별 처리 내역 응답의 슬롯 키.
const REQUEST_ITEM_SLOT: Record<
  PathPickRequestItemKind,
  keyof Pick<
    PathPickProductRequestItem,
    | "naverSyncRequest"
    | "naverExcelRequest"
    | "kreamExcelRequest"
    | "kreamExcelConfirmedQuantityRequest"
  >
> = {
  NAVER_SYNC: "naverSyncRequest",
  NAVER_EXCEL: "naverExcelRequest",
  KREAM_EXCEL: "kreamExcelRequest",
  KREAM_EXCEL_CONFIRMED_QUANTITY: "kreamExcelConfirmedQuantityRequest",
};

// 상품별 처리 내역의 채널 줄 — 표시 순서와 각 줄이 볼 종류를 여기서 고정한다.
// KREAM 파일은 확정 수량 신청이 기존 신청을 대체하는 개념이라 줄을 하나로 합친다:
// 확정 수량 건이 있으면 그것을 보여주고, 없으면 이전에 쌓인 KREAM_EXCEL 건을 보여준다.
const REQUEST_CHANNELS: {
  channel: PathPickProductRequestChannel;
  kinds: PathPickRequestItemKind[];
}[] = [
  { channel: "smartStoreSync", kinds: ["NAVER_SYNC"] },
  { channel: "naverExcel", kinds: ["NAVER_EXCEL"] },
  {
    channel: "kreamExcel",
    kinds: ["KREAM_EXCEL_CONFIRMED_QUANTITY", "KREAM_EXCEL"],
  },
];

// 채널 줄이 표시할 슬롯 하나를 고른다 — 앞선 종류가 있으면 그것으로 끝낸다.
// 미신청 종류는 슬롯이 null이라, 모두 없으면 줄을 만들지 않는다는 뜻으로 null을 돌려준다.
const resolveChannelSlot = (
  item: PathPickProductRequestItem,
  kinds: PathPickRequestItemKind[]
): {
  kind: PathPickRequestItemKind;
  result: PathPickProductRequestResult;
} | null => {
  for (const kind of kinds) {
    const result = item[REQUEST_ITEM_SLOT[kind]];
    if (result) {
      return { kind, result };
    }
  }
  return null;
};

// 항목 상태 → 모달 표시용 상태.
const ITEM_STATUS_TO_CHANNEL_STATUS: Record<
  PathPickRequestItemStatus,
  PathPickProductRequestStatus
> = {
  SUCCESS: "completed",
  FAILED: "failed",
  QUOTA_EXCEEDED: "quotaExceeded",
};

// 채널 결과 상태 판정 — 서버가 내려준 상태를 그대로 쓴다(사유는 사유 컬럼이 따로 보여준다).
const toChannelStatus = (
  result: PathPickProductRequestResult
): PathPickProductRequestStatus => {
  // 값 없음 — 아직 처리 전이다.
  if (!result.requestStatus) {
    return "processing";
  }
  return ITEM_STATUS_TO_CHANNEL_STATUS[result.requestStatus] ?? "processing";
};

// 처리결과 슬롯 → 모달 표시용 채널 결과 배열. 신청 이력이 없는 채널은 줄을 만들지 않는다.
export const toProductRequestChannelResults = (
  item: PathPickProductRequestItem
): PathPickProductRequestChannelResult[] =>
  REQUEST_CHANNELS.flatMap(({ channel, kinds }) => {
    const slot = resolveChannelSlot(item, kinds);
    if (!slot) {
      return [];
    }
    const { kind, result } = slot;
    return [
      {
        channel,
        kind,
        status: toChannelStatus(result),
        syncStatus: result.syncStatus,
        syncDeletedBy: result.syncDeletedBy ?? null,
        errorType: result.errorType,
        requestedAt: result.requestedAt,
        updatedAt: result.updatedAt,
      },
    ];
  });

// 표시용 상태 → 배지 표기. 요청 단위 표기(REQUEST_STATUS_META)를 그대로 참조해
// 신청 상품 내역 모달과 문구가 갈릴 여지를 없앤다.
const PRODUCT_REQUEST_STATUS_META: Record<
  PathPickProductRequestStatus,
  RequestStatusMetaByKind
> = {
  completed: REQUEST_STATUS_META.COMPLETED,
  failed: REQUEST_STATUS_META.FAILED,
  quotaExceeded: REQUEST_STATUS_META.QUOTA_EXCEEDED,
  processing: PROCESSING_META_BY_KIND,
};

// (연동 채널이면 "API 연동 …", 엑셀 채널이면 "파일 생성 …" 문구가 나온다)
export const getProductRequestChannelStatusMeta = (
  channel: PathPickProductRequestChannel,
  status: PathPickProductRequestStatus
): PathPickRequestStatusMeta => {
  const meta = PRODUCT_REQUEST_STATUS_META[status] ?? PROCESSING_META_BY_KIND;
  return channel === "smartStoreSync" ? meta.sync : meta.download;
};

// 연동 상태 → 배지 표기. 해제/삭제는 종료 상태라 처리 상태 배지("API 연동 완료" 등)를 덮는다.
// 연동 중이거나 값이 없으면 null을 돌려주므로 호출부는 `?? 처리상태meta` 로 폴백한다.
// 연동 상태 → 배지 표기. null은 "연동 중이라 처리 상태 배지를 그대로 쓴다"는 뜻이다.
// syncStatus는 연동 채널에만 실리므로 채널 분기 없이 연동 문구를 쓴다.
const SYNC_STATUS_META: Record<
  PathPickProductSyncStatus,
  PathPickRequestStatusMeta | null
> = {
  registered: null,
  synced: null,
  failed: {
    labelKey: "status_sync_failed",
    colorVariant: "red",
    leadingIcon: "warning-circle",
  },
  deleted: { labelKey: "status_deleted", colorVariant: "gray" },
  disconnected: { labelKey: "status_disconnected", colorVariant: "gray" },
};

// 판매자가 스토어에서 직접 지운 상품 — 우리가 내린 "상품 삭제"와 문구를 나눈다.
// 사용자는 지운 적이 없는데 "상품 삭제"로 보이면 패스레이드가 지운 것으로 읽힌다.
const DELETED_IN_STORE_META: PathPickRequestStatusMeta = {
  labelKey: "status_deleted_in_store",
  colorVariant: "gray",
};

export const getPathPickSyncStatusMeta = (
  syncStatus?: PathPickProductSyncStatus | null,
  syncDeletedBy?: PathPickProductSyncDeletedBy | null
): PathPickRequestStatusMeta | null => {
  if (!syncStatus) {
    return null;
  }
  if (syncStatus === "deleted" && syncDeletedBy === "STORE") {
    return DELETED_IN_STORE_META;
  }
  return SYNC_STATUS_META[syncStatus] ?? null;
};

// 연동 중(=연동 해제 가능)으로 보는 연동 상태.
// registered는 스토어에 상품이 살아 있는 상태라 해제 대상이고, failed는 해제할 연동 자체가 없다.
const IS_SYNC_ACTIVE: Record<PathPickProductSyncStatus, boolean> = {
  registered: true,
  synced: true,
  failed: false,
  deleted: false,
  disconnected: false,
};

// 연동 중(=연동 해제 가능) 판정. 연동 요청이 성공했고 아직 해제/삭제되지 않은 상품만 해제 대상이다.
// syncStatus 부재는 연동 중으로 본다 — 기존 동작(요청 성공 여부)과 같아진다.
export const isProductSyncActive = (item: PathPickProductRequestItem) =>
  item.naverSyncRequest?.requestStatus === "SUCCESS" &&
  (IS_SYNC_ACTIVE[item.naverSyncRequest.syncStatus ?? "synced"] ?? false);

// 신청 상품 내역(요청 상세) 행의 연동 중 판정 — 위와 같은 기준을 상세 응답 형태에 맞춘 것이다.
export const isRequestDetailItemSyncActive = (
  product: PathPickRequestDetailItem
) =>
  product.status === "SUCCESS" &&
  (IS_SYNC_ACTIVE[product.syncStatus ?? "synced"] ?? false);

// 아직 처리 전(= 슬롯은 있는데 처리 결과가 없는) 채널이 하나라도 있으면 폴링을 계속한다.
export const hasPendingProductRequest = (
  data?: PathPickProductRequestListResponse
) =>
  (data?.items ?? []).some((item) =>
    REQUEST_CHANNELS.some(({ kinds }) => {
      // 슬롯이 하나도 없으면 미신청 채널이라 기다릴 것이 없다.
      const slot = resolveChannelSlot(item, kinds);
      return Boolean(slot) && !slot?.result.requestStatus;
    })
  );

// 다시 신청해도 같은 결과가 나오는 실패 사유 — 그 행에는 재생성 버튼을 두지 않는다.
// 사유 코드가 늘면 여기서 컴파일 에러를 내 재시도 가능 여부를 정하고 지나가게 한다.
const IS_ERROR_RETRYABLE: Record<PathPickRequestErrorType, boolean> = {
  // 상품 정보를 보완하거나 일시 오류가 풀리면 성공할 수 있다.
  MADE_IN_MISSING: true,
  CATEGORY_MISSING: true,
  IMAGE_MISSING: true,
  IMAGE_UNREACHABLE: true,
  IMAGE_INVALID: true,
  STORE_NOT_CONNECTED: true,
  STORE_CONFIG_MISSING: true,
  NAVER_AUTH_FAILED: true,
  NAVER_IMAGE_UPLOAD_FAILED: true,
  NAVER_API_ERROR: true,
  TEMPLATE_MISSING: true,
  INTERNAL_ERROR: true,
  // 사이즈 표기를 고치면 올라간다.
  OPTION_INVALID: true,
  MARKET_AUTH_FAILED: true,
  MARKET_API_ERROR: true,
  // 조건 자체가 성립하지 않아 지금 다시 신청해도 같은 결과가 나온다.
  KREAM_UNMATCHED: false,
  NO_SELLABLE_OPTION: false,
  ALREADY_SYNCED: false,
  NO_ELIGIBLE_PRODUCT: false,
  QUOTA_EXCEEDED: false,
  // 마켓에 브랜드가 생기거나, 같은 상품으로 본 기존 상품이 내려가야 풀린다.
  BRAND_NOT_FOUND: false,
  DUPLICATE_IN_MARKET: false,
};

// 사유가 없거나(null) 아직 매핑되지 않은 코드는 재시도 가능으로 본다 — 길을 막지 않는 쪽이 안전하다.
export const isRetryableErrorType = (
  errorType: PathPickRequestErrorType | null
) => (errorType ? IS_ERROR_RETRYABLE[errorType] ?? true : true);

// 실패 행 비고 문구 — 사용자가 이유를 알면 되는 사유만 따로 보여 주고, 나머지는 문의 안내로 묶는다.
// (서버 오류 코드는 사용자가 스스로 조치할 수 없어 그대로 노출하지 않는다)
export type PathPickFailureNoteKey =
  | "note_contact_support"
  | "note_image_unavailable"
  | "note_brand_not_in_market";

const FAILURE_NOTE_KEY: Partial<
  Record<PathPickRequestErrorType, PathPickFailureNoteKey>
> = {
  IMAGE_MISSING: "note_image_unavailable",
  IMAGE_UNREACHABLE: "note_image_unavailable",
  IMAGE_INVALID: "note_image_unavailable",
  BRAND_NOT_FOUND: "note_brand_not_in_market",
};

export const getFailureNoteKey = (
  errorType: PathPickRequestErrorType | null
): PathPickFailureNoteKey =>
  (errorType && FAILURE_NOTE_KEY[errorType]) || "note_contact_support";

// 배지 문구가 실패인지 — 실패 판정을 배지 표기와 한 곳에서 맞춘다.
// (연동 실패 / 파일 생성 실패. 한도 초과는 별도 표기라 실패로 보지 않는다.)
const FAILED_STATUS_LABEL_KEYS = ["status_sync_failed", "status_file_failed"];

export const isFailedStatusMeta = (meta: PathPickRequestStatusMeta) =>
  FAILED_STATUS_LABEL_KEYS.includes(meta.labelKey);

// 배지가 실패로 보이는 행 판정 — 연동 상태가 처리 상태 배지를 덮으므로 ChannelBadge와 같은 순서로 본다.
export const isChannelResultFailed = (
  result: PathPickProductRequestChannelResult
) => {
  const syncMeta = getPathPickSyncStatusMeta(
    result.syncStatus,
    result.syncDeletedBy
  );
  return syncMeta ? isFailedStatusMeta(syncMeta) : result.status === "failed";
};

// 이 줄을 다시 신청할 수 있는지 — 재생성 버튼 노출과 선택 가능 판정이 함께 쓴다.
// 확정 수량 신청은 주문 id로만 접수할 수 있는데 이 응답에는 주문 id가 없어 제외한다
// (사용자는 주문 상세의 KREAM 입찰용 파일 신청에서 다시 신청한다).
export const isChannelResultRetryable = (
  result: PathPickProductRequestChannelResult
) =>
  isChannelResultFailed(result) &&
  isRetryableErrorType(result.errorType) &&
  result.kind !== "KREAM_EXCEL_CONFIRMED_QUANTITY";

// 표시 중인 채널 줄에서 재생성 대상 request_types를 뽑는다.
// 입력은 구분 필터가 좁힌 visibleResults다 — 사용자가 보고 있는 줄만 재생성 대상이 된다.
export const toRetryableKinds = (
  results: PathPickProductRequestChannelResult[]
): PathPickProductRequestKind[] =>
  results.filter(isChannelResultRetryable).map((result) => result.kind);

// 옵션(사이즈)별 재고의 합 = 상품의 현재 재고.
// 명세상 quantity가 nullable이라 값이 없는 옵션은 0으로 본다.
export const getTotalOptionQuantity = (
  options: PathPickItemOption[] | undefined
) => (options ?? []).reduce((sum, option) => sum + (option.quantity ?? 0), 0);
